'use client';
import React, { useMemo } from 'react';
import classNames from 'classnames';
import styles from './NewPartialArticleLayout.module.scss';

import {
  ArticleData,
  generatePreviewFromBody,
  getCanonicalUrl,
  //printProHeadlineArticleContent,
} from '@benzinga/article-manager';

import { Button, ButtonVariant } from '@benzinga/core-ui';

import { truncate } from '@benzinga/utils';
import { NodeLayout } from '@benzinga/content-manager';
import { TrackingManager } from '@benzinga/tracking-manager';
import { SessionContext } from '@benzinga/session-context';
import { sanitizeHTML } from '@benzinga/frontend-utils';

export interface NewPartialArticleLayoutProps {
  articleID?: number;
  articleData: ArticleData;
  deviceType?: string | null;
  articleScrollViewMoreLink?: string;
  googleNewsUrlKey?: string;
  isAmp?: boolean;
  isBot?: boolean;
  lastStory?: boolean;
  layout?: NodeLayout;
  loadMoreButtonVariant?: ButtonVariant;
  showAdvertiserDisclosure?: boolean;
  executeImpressionEvent?: boolean;
  executeReplaceHistory?: boolean;
  executePageviewEvent?: boolean;
  showCommentButton?: boolean;
  showFontAwesomeIcons?: boolean;
  showWhatsAppIcon?: boolean;
  trackMeta?: boolean;
  isPaywallActive?: boolean;
  isNotPaywalled?: boolean;
}

export const NewPartialArticleLayout: React.FC<NewPartialArticleLayoutProps> = ({ articleData }) => {
  const session = React.useContext(SessionContext);

  const previewText = useMemo(() => {
    const result = articleData?.body ? generatePreviewFromBody(articleData?.body, 70) : articleData?.teaserText;
    return result;
  }, [articleData?.body, articleData?.teaserText]);

  const showAdvertiserDisclosure = articleData?.meta?.Flags?.ShowAdvertiserDisclosure;
  const title = articleData?.isHeadline ? truncate(articleData.title ?? '', 45) : articleData.title;
  const articleUrl = getCanonicalUrl(articleData ?? {});

  const handleReadMore = () => {
    const sponsored = articleData?.meta?.Flags?.ShowAdvertiserDisclosure;
    session.getManager(TrackingManager).trackCampaignEvent('click', {
      partner_id: (articleData?.meta?.Reach?.Disclosure?.tid || 0).toString(),
      sponsored: String(sponsored),
      unit_type: `article-infinite-scroll-${articleData?.nodeId}`,
    });
  };

  return (
    <div className={classNames(styles.container, 'partial-article-layout flex flex-col text-black')}>
      <div className="background-cover-pattern"></div>
      <div className="p-4">
        <a className="block" href={articleUrl} onClick={handleReadMore}>
          <div className="flex flex-col md:flex-row gap-6">
            {showAdvertiserDisclosure && (
              <div className="w-full flex justify-end md:hidden mb-[-11px] mt-[-2px]">
                <div className="advertiser-disclosure-label">Partner Disclosure</div>
              </div>
            )}
            {articleData?.image && (
              <div className="md:w-1/4 flex-shrink-0">
                <img alt={title} className="w-full h-32 md:h-28 object-cover rounded-lg" src={articleData.image} />
              </div>
            )}
            <div className={articleData?.image ? 'md:w-3/4 -mt-1' : 'w-full'}>
              {showAdvertiserDisclosure && (
                <div className="w-full justify-end hidden md:flex md:mt-[-15px]">
                  <div className="advertiser-disclosure-label">Partner Disclosure</div>
                </div>
              )}
              <h2 className="text-2xl line-clamp-3 font-normal">{title}</h2>
              <div
                className={articleData?.isHeadline ? undefined : 'preview-text'}
                // dangerouslySetInnerHTML={{
                //   __html: articleData?.isHeadline ? printProHeadlineArticleContent() : previewText || '',
                // }}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHTML(previewText || ''),
                }}
              />
            </div>
          </div>
        </a>
      </div>
      <Button
        as="a"
        className="w-full font-semibold read-more-button"
        href={articleUrl}
        onClick={handleReadMore}
        variant="flat-light-blue"
      >
        READ MORE
      </Button>
    </div>
  );
};
